import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { NewspaperIcon, SpinnerGapIcon, CopyIcon, ArrowClockwiseIcon, XLogoIcon, LockKeyIcon, ImageIcon } from "@phosphor-icons/react";
import { fetchEditorialIndex, fetchEditorial } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import CustomStory from "../components/CustomStory";
import StoryScheduler from "../components/StoryScheduler";
import { HypeMachine } from "../components/HypeMachine";

// Minimal, safe markdown → React (headings, hr, bold, italic, paragraphs).
const inline = (text, kp) => {
  const parts = [];
  let rest = text;
  let idx = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/;
  let m;
  while ((m = re.exec(rest))) {
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2] != null) parts.push(<strong key={`${kp}-b${idx}`} className="text-white font-semibold">{m[2]}</strong>);
    else parts.push(<em key={`${kp}-i${idx}`} className="text-white/90">{m[3]}</em>);
    rest = rest.slice(m.index + m[0].length);
    idx++;
  }
  if (rest) parts.push(rest);
  return parts;
};

const renderMd = (md) =>
  md.split(/\n{2,}/).map((blk, i) => {
    const t = blk.trim();
    if (!t) return null;
    if (t === "---" || t === "***") return <hr key={i} className="border-white/10 my-4" />;
    if (t.startsWith("### ")) return <h3 key={i} className="font-heading font-bold text-lg mt-4 mb-1">{inline(t.slice(4), i)}</h3>;
    if (t.startsWith("## ")) return <h2 key={i} className="font-heading font-black text-2xl tracking-tight mb-2">{inline(t.slice(3), i)}</h2>;
    if (t.startsWith("# ")) return <h2 key={i} className="font-heading font-black text-2xl tracking-tight mb-2">{inline(t.slice(2), i)}</h2>;
    return <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{t.split("\n").map((ln, j) => <span key={j}>{inline(ln.replace(/^[-*]\s/, "• "), `${i}-${j}`)}<br /></span>)}</p>;
  });

const ago = (ts) => {
  if (!ts) return "never";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Drop a trailing timestamp/date segment ("… — 21:03 UTC, 09 Aug 2026") for a clean X headline.
const cleanHeadline = (title = "") => {
  const parts = title.split(/\s+[—–-]\s+/);
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    if (/UTC|\b20\d{2}\b|\d{1,2}:\d{2}/.test(last)) return parts.slice(0, -1).join(" — ").trim();
  }
  return title.trim();
};

// The punchy lede — first sentence(s) of the body up to ~170 chars.
const pullHook = (body = "") => {
  const plain = body.replace(/^#.*$/gm, "").replace(/[#*_>`]/g, "").replace(/\s+/g, " ").trim();
  const sentences = plain.split(/(?<=[.!?])\s+/);
  let out = "";
  for (const s of sentences) {
    const t = s.trim();
    if (!t) continue;
    if (out && (out + " " + t).length > 170) break;
    out = out ? `${out} ${t}` : t;
    if (out.length >= 60) break;
  }
  if (!out) out = plain.slice(0, 167);
  return out.length > 170 ? out.slice(0, 167) + "…" : out;
};

export default function Newsroom() {
  useSeo({ title: "Newsroom · AlphaApeing", description: "AlphaApeing's private newsroom — data-first meme-coin takes ready to post to X." });
  const { user, openAuth } = useAuth();
  const isAdmin = user?.role === "admin";
  const [active, setActive] = useState(null);
  const cardRef = useRef(null);

  const idxQ = useQuery({ queryKey: ["editorialIndex"], queryFn: fetchEditorialIndex, enabled: isAdmin });
  useEffect(() => {
    if (!active && idxQ.data?.formats?.length) setActive(idxQ.data.formats[0].slug);
  }, [idxQ.data, active]);

  const pieceQ = useQuery({
    queryKey: ["editorial", active],
    queryFn: () => fetchEditorial(active),
    enabled: !!active && isAdmin,
    staleTime: 300000,
  });

  const piece = pieceQ.data;
  const activeMeta = (idxQ.data?.formats || []).find((f) => f.slug === active);
  const headline = piece?.body ? cleanHeadline(piece.title) : "";
  const hook = piece?.body ? pullHook(piece.body) : "";
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://alphaapeing.com";

  const copy = () => {
    if (!piece?.body) return;
    navigator.clipboard.writeText(piece.body).then(() => toast.success("Copied — paste it into your post")).catch(() => toast.error("Copy failed"));
  };

  const regenerate = async () => {
    if (!active) return;
    toast.loading("Rewriting…", { id: "regen" });
    try {
      await fetchEditorial(active, true);
      await pieceQ.refetch();
      toast.success("Rewritten", { id: "regen" });
    } catch {
      toast.error("Rewrite failed", { id: "regen" });
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    toast.loading("Building card…", { id: "card" });
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, width: 1200, height: 675 });
      const a = document.createElement("a");
      a.download = `alphaapeing-${active}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Card downloaded — attach it to your post", { id: "card" });
    } catch (e) {
      toast.error("Couldn't build the card", { id: "card" });
    }
  };

  const postToX = () => {
    if (!headline) return;
    const text = `${headline}\n\n📊 via @AlphaApingrules`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ---- Locked gate for non-admins ----
  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-24 text-center animate-fade-up" data-testid="newsroom-locked">
        <LockKeyIcon size={40} weight="fill" className="text-neon mx-auto mb-5" />
        <h1 className="font-heading font-black text-3xl tracking-tight mb-3">Newsroom is private</h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-8">
          This page is locked to the AlphaApeing operator. Sign in with the admin account to write and share pieces.
        </p>
        <button
          data-testid="newsroom-signin-btn"
          onClick={() => openAuth("login", "", "the Newsroom")}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold hover:bg-neon/90 transition-colors"
        >
          <LockKeyIcon size={16} weight="fill" /> Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-up">
      <div>
        <div className="flex items-center gap-2.5">
          <NewspaperIcon size={30} weight="fill" className="text-neon" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">NEWSROOM</h1>
          <span className="ml-1 flex items-center gap-1 px-2 py-0.5 border border-neon/30 text-neon text-[10px] font-mono uppercase tracking-wide"><LockKeyIcon size={11} weight="fill" /> Private</span>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          AlphaApeing's take on the tape — sharp, skeptical, and data-first. Auto-written from live on-chain signals,
          failures included. Copy the text, download a branded card, or fire it straight to X.
        </p>
      </div>

      <HypeMachine />
      <CustomStory />
      <StoryScheduler />

      <div className="pt-2">
        <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground/70 mb-3">Or pick an auto-written format</p>
        <div className="flex flex-wrap gap-2" data-testid="newsroom-tabs">
        {(idxQ.data?.formats || []).map((f) => (
          <button
            key={f.slug}
            data-testid={`newsroom-tab-${f.slug}`}
            onClick={() => setActive(f.slug)}
            className={`flex items-center gap-2 px-3.5 py-2 text-sm font-semibold border transition-colors ${
              active === f.slug ? "border-neon/60 bg-neon/[0.08] text-neon" : "border-white/12 text-muted-foreground hover:border-white/30 hover:text-white"
            }`}
          >
            <span>{f.emoji}</span> {f.title}
          </button>
        ))}
        </div>
      </div>

      <div className="border border-white/10 bg-card p-6 sm:p-8 min-h-[300px]" data-testid="newsroom-article">
        {pieceQ.isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground font-mono text-sm">
            <SpinnerGapIcon size={26} className="animate-spin text-neon" /> Writing the story…
          </div>
        ) : piece?.pending || !piece?.body ? (
          <div className="text-center py-16 text-muted-foreground">
            <NewspaperIcon size={32} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">{piece?.note || "Not enough live data to write this one yet — check back shortly."}</p>
          </div>
        ) : (
          <>
            {piece?.stale && (
              <div data-testid="newsroom-stale-banner" className="flex items-center gap-2 mb-4 px-3 py-2 border font-mono text-[11px]" style={{ borderColor: "#F0A02055", background: "#F0A02010", color: "#e8b96a" }}>
                ⚠️ Couldn't write a fresh edition just now (data source quiet or rate-limited) — showing the last one from {ago(piece.generatedAt)}. It retries automatically.
              </div>
            )}
            <div className="prose-degen">{renderMd(piece.body)}</div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-4 border-t border-white/10">
              <span className="text-[11px] font-mono text-muted-foreground">Published {ago(piece.generatedAt)} · auto-written from live data</span>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid="newsroom-post-x-btn" onClick={postToX} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-neon text-black hover:bg-neon/90 transition-colors">
                  <XLogoIcon size={14} weight="fill" /> Post to X
                </button>
                <button data-testid="newsroom-card-btn" onClick={downloadCard} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                  <ImageIcon size={14} /> Share card
                </button>
                <button data-testid="newsroom-copy-btn" onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                  <CopyIcon size={14} /> Copy
                </button>
                <button data-testid="newsroom-regen-btn" onClick={regenerate} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                  <ArrowClockwiseIcon size={14} /> Rewrite
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
        Pieces are generated by AI from AlphaApeing's live on-chain data and refreshed periodically. Signals & evidence, not predictions — not financial advice.
      </p>

      {/* Off-screen branded share card (1200×675) captured to PNG */}
      {piece?.body && (
        <div style={{ position: "fixed", left: "-10000px", top: 0, pointerEvents: "none" }} aria-hidden>
          <div
            ref={cardRef}
            data-testid="newsroom-share-card"
            style={{ width: 1200, height: 675, backgroundColor: "#05070c" }}
            className="relative overflow-hidden flex flex-col justify-between"
          >
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(1100px 500px at 85% -10%, rgba(0,255,136,0.16), transparent 60%)" }} />
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, backgroundColor: "#00ff88" }} />
            <div style={{ position: "relative", padding: "64px 72px 0 80px" }} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span style={{ width: 18, height: 18, borderRadius: 9999, backgroundColor: "#00ff88", boxShadow: "0 0 20px rgba(0,255,136,0.9)" }} />
                <span className="font-heading font-black" style={{ fontSize: 30, letterSpacing: "-0.02em", color: "#ffffff" }}>ALPHAAPEING</span>
                <span className="font-mono" style={{ fontSize: 15, color: "#00ff88", letterSpacing: "0.12em" }}>· DEGEN SCORE</span>
              </div>
              <span className="font-mono uppercase" style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", letterSpacing: "0.12em" }}>
                {activeMeta?.emoji} {activeMeta?.title}
              </span>
            </div>
            <div style={{ position: "relative", padding: "0 80px" }}>
              <h2 className="font-heading font-black" style={{ fontSize: 62, lineHeight: 1.04, letterSpacing: "-0.03em", color: "#ffffff", maxHeight: 340, overflow: "hidden" }}>
                {headline}
              </h2>
              {hook && (
                <p style={{ marginTop: 28, fontSize: 27, lineHeight: 1.4, color: "#9defc4", maxWidth: 1040, maxHeight: 120, overflow: "hidden" }}>
                  {hook}
                </p>
              )}
            </div>
            <div style={{ position: "relative", padding: "0 80px 60px 80px" }} className="flex items-center justify-between">
              <span className="font-mono" style={{ fontSize: 22, color: "#ffffff", fontWeight: 700 }}>alphaapeing.com</span>
              <span className="font-mono" style={{ fontSize: 20, color: "rgba(255,255,255,0.55)" }}>@AlphaApingrules</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
